import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Label,
  ModalBody,
} from "@tandemdiabetes/sugarmate-react-components"
import { useEffect } from "react"

import { useEntryModalPrivateContext } from "../EntryModalPrivateContext"
import { SubEntryFooter } from "../SubEntryFooter"
import { SubEntryHeader } from "../SubEntryHeader"
import { useUpdateActivity } from "../useUpdateActivity"
import { AccountInsulinButton } from "./AccountInsulinButton"
import { accountInsulinToActivityInsulin } from "./accountInsulinToActivityInsulin"
import { InsulinFormNameSelect } from "./InsulinFormNameSelect"
import { InsulinFormUnitsRangeInput } from "./InsulinFormUnitsRangeInput"
import { useOnAccountInsulinCreated } from "./useOnAccountInsulinCreated"
import { useOnCurrentAccountInsulinDeleted } from "./useOnCurrentAccountInsulinDeleted"
import { useOnCurrentAccountInsulinUpdated } from "./useOnCurrentAccountInsulinUpdated"
import { AccountInsulinModal } from "src/components/AccountInsulinModal"
import { AccountInsulinLoadingProvider } from "src/contexts/LoadingContexts"
import {
  useAccountInsulin,
  useAccountInsulins,
} from "src/hooks"
import styles from "src/styles/entryModal.module.scss"
import type {
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

interface Props {
  currentSubEntry: EditableSubEntry<ActivityType.Insulin>;
  updateSubEntry: (s: Partial<EditableSubEntry<ActivityType.Insulin>>) => void;
  currentUserEntry: EditableUserEntry;
  setCurrentUserEntry: (userEntry: EditableUserEntry) => void;
}

/*
 * renders the form for editing a insulin subentry
 */
export function InsulinForm(props: Props) {
  const {
    currentSubEntry,
    updateSubEntry,
    currentUserEntry,
    setCurrentUserEntry,
  } = props

  const { insulin: activity } = currentSubEntry

  const updateActivity = useUpdateActivity(
    ActivityType.Insulin,
    activity,
    updateSubEntry,
  )

  const { readOnly } = useEntryModalPrivateContext()

  const { result: accountInsulins } = useAccountInsulins()
  const onlyAccountInsulin = accountInsulins.length === 1 ? accountInsulins[0] : undefined

  // initialize the activity to the only account insulin if the user only has one
  useEffect(
    () => {
      if (onlyAccountInsulin && !currentSubEntry.insulin?.insulin_med) {
        updateActivity({
          insulin_med: accountInsulinToActivityInsulin(onlyAccountInsulin),
        })
      }
    },
    [
      currentSubEntry.insulin,
      updateActivity,
      onlyAccountInsulin,
    ],
  )

  // the account insulin currently referenced in the insulin activity
  const currentAccountInsulin = useAccountInsulin(
    activity?.insulin_med?.account_insulin_id ?? -1,
  )

  // if the current account insulin is updated, upate the activity too
  useOnCurrentAccountInsulinUpdated(
    activity,
    updateActivity,
    currentAccountInsulin,
  )

  // if a new account insulin is created then use it for the activity
  useOnAccountInsulinCreated(
    activity,
    updateActivity,
  )

  // reset the activity if it is a new activity and its account insulin is removed
  useOnCurrentAccountInsulinDeleted(
    activity,
    updateActivity,
    currentAccountInsulin,
    currentSubEntry,
  )

  return (
    <>
      <SubEntryHeader
        activityType={ActivityType.Insulin}
        currentSubEntry={currentSubEntry}
        currentUserEntry={currentUserEntry}
        setCurrentUserEntry={setCurrentUserEntry}
        readOnly={readOnly}
        closeBtnId="insulin-entry-close-btn"
      />
      <ModalBody className={styles.modalBody}>
        <InsulinFormUnitsRangeInput
          activity={activity}
          updateActivity={updateActivity}
          readOnly={readOnly}
        />
        {readOnly ? (
          activity?.insulin_med?.name ? (
            <Label>
              {activity.insulin_med.name}
            </Label>
          ) : null
        ) : (
          <div className={styles.row}>
            <AccountInsulinLoadingProvider>
              <InsulinFormNameSelect
                activity={activity}
                updateActivity={updateActivity}
              />
            </AccountInsulinLoadingProvider>
            <AccountInsulinModal>
              <AccountInsulinButton
                currentAccountInsulin={currentAccountInsulin}
              />
            </AccountInsulinModal>
          </div>
        )}
        <SubEntryFooter
          currentSubEntry={currentSubEntry}
          currentUserEntry={currentUserEntry}
          updateSubEntry={updateSubEntry as (s: Partial<EditableSubEntry<SubEntryType>>) => void}
          readOnly={readOnly}
        />
      </ModalBody>
    </>
  )
}
